import { Clock, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EID } from '@/lib/element-ids.registry'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'
import { ReauditButton } from './ReauditButton'
import { ScoreDashboard } from './ScoreDashboard'
import { ShareToggle } from './ShareToggle'

interface ListingReportHeaderProps {
  report: ListingAuditReport
  jobId?: string
  /** Hide the share control (e.g. on the public, read-only view). Defaults to true. */
  showShare?: boolean
  /** Audit run duration in minutes (only known on the authenticated detail view). */
  durationMinutes?: number
}

/** Report header: product thumbnail, title, share toggle and overall score dashboard. */
export function ListingReportHeader({ report, jobId, showShare = true, durationMinutes }: ListingReportHeaderProps) {
  const { t } = useTranslation()
  // Only ASIN-based audits link out to Amazon; manual / new_product audits have no real ASIN.
  const hasAmazonListing = report.audit_mode !== 'new_product' && !!report.asin
  return (
    <header className="listing-report-header flex flex-col gap-4">
      <div className="flex items-center gap-4">
        {report.main_image_url && (
          <img
            src={report.main_image_url}
            alt={report.product_title ?? ''}
            className="w-16 h-16 rounded-lg object-cover border border-neutral-200 dark:border-neutral-700 flex-shrink-0"
          />
        )}
        {report.product_title && (
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 min-w-0 line-clamp-2">
            {report.product_title}
          </h2>
        )}
        <div className="ml-auto flex flex-shrink-0 items-center gap-3">
          {durationMinutes != null && (
            <span className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
              <Clock className="h-3.5 w-3.5" />
              {t('listingBuilder.report.durationMinutes', { count: durationMinutes })}
            </span>
          )}
          {hasAmazonListing && (
            <a
              id={EID['listing-builder-report-amazon-link']}
              href={`https://www.amazon.${report.tld || 'de'}/dp/${report.asin}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Amazon"
              className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-orange-500 dark:text-neutral-400"
            >
              <span className="hidden sm:inline">Amazon</span>
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          {jobId && <ReauditButton jobId={jobId} />}
          {showShare && jobId && <ShareToggle jobId={jobId} />}
        </div>
      </div>
      {/* new_product audits carry scores: null (no existing listing to grade). */}
      {report.scores && <ScoreDashboard scores={report.scores} />}
    </header>
  )
}
